import React, { useEffect, useMemo, useRef, useState } from 'react';
import './allergy-history.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
    patientAddSubDocument,
    patientRemoveSubDocument,
    patientUpdateSubDocument
} from '../../../service/api/patient-api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons';
import CustomModal from '../../common/modal/modal';
import { getAllergyList, getDiseaseList } from '../../../store/slices/visitSlice';
import { post } from '../../../utils/fetchAPI';

const AllergyHistory = ({ patientId, visitDetails, patientDetails, updateState }) => {
    const { allergyList } = useSelector((state) => state.visit);
    const [patientAllergyHistory, setPatientAllergyHistory] = useState([]);
    const [allergyListWithType, setAllergyListWithType] = useState([]);
    const [info, setInfo] = useState({});
    const timeoutIdRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false)
    const [type, setType] = useState('medicine')
    const dispatch = useDispatch()

    useEffect(() => {
        if (patientDetails?.allergyHistories) {
            const history = patientDetails?.allergyHistories;
            setPatientAllergyHistory(history);

            const obj = {};
            history.forEach((item) => {
                obj[item.allergy] = {
                    allergy: item.allergy,
                    reaction: item.reaction,
                    severity: item.severity,
                    source: item.source
                };
            });
            setInfo(obj);
        }
    }, [patientDetails?.allergyHistories]);

    useMemo(() => {
        if (allergyList) {
            const groupedByType = {};
            allergyList.forEach((obj) => {
                const type = obj.type;
                if (!groupedByType[type]) groupedByType[type] = [];
                groupedByType[type].push(obj);
            });
            const result = Object.entries(groupedByType).map(([type, allergy]) => ({
                type,
                allergy
            }));
            setAllergyListWithType(result);
        }
    }, [allergyList]);

    const handleAllergyChange = (id, key, value, checked, item, type) => {
        if (key === 'allergy') {
            if (checked) {
                const newData = {
                    allergy: id,
                    reaction: '',
                    severity: '',
                    source: '',
                    item,
                    type
                };
                setInfo((prev) => ({ ...prev, [id]: newData }));
                onChangeAdd(id, newData);
            } else {
                setInfo((prev) => {
                    const updated = { ...prev };
                    delete updated[id];
                    return updated;
                });
                onRemove(id);
            }
        } else {
            const updated = {
                ...info[id],
                [key]: value
            };
            setInfo((prev) => ({ ...prev, [id]: updated }));
            if (info[id]) {
                onUpdate(id, updated);
            } else {
                const newData = {
                    allergy: id,
                    reaction: key === 'reaction' ? value : '',
                    severity: key === 'severity' ? value : '',
                    source: key === 'source' ? value : '',
                    item,
                    type
                };
                setInfo((prev) => ({ ...prev, [id]: newData }));
                onChangeAdd(id, newData);
            }
        }
    };

    const onChangeAdd = async (key, payload) => {
        await patientAddSubDocument('allergyHistories', payload, visitDetails?.patient || patientId)
            .then((res) => {
                if (res?.status === 200) updateState(res?.data);
            })
            .catch((err) => console.log(err));
    };

    const onRemove = async (key) => {
        await patientRemoveSubDocument('allergyHistories', { allergy: key }, visitDetails?.patient || patientId)
            .then((res) => {
                if (res?.status === 200) updateState(res?.data);
            })
            .catch((err) => console.log(err));
    };

    const onUpdate = async (key, payload) => {
        const body = {
            payload: {
                'allergyHistories.$.reaction': payload?.reaction,
                'allergyHistories.$.severity': payload?.severity,
                'allergyHistories.$.source': payload?.source
            },
            filterKey: 'allergyHistories.allergy',
            filterKeyValue: key
        };

        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = setTimeout(() => {
            patientUpdateSubDocument(body, visitDetails?.patient || patientId)
                .then((res) => {
                    if (res?.status === 200) updateState(res?.data);
                })
                .catch((err) => console.log(err));
        }, 500);
    };

    const contentHideFun = (id) => {
        const icon = document?.getElementById(`${id}-icon`);
        const table = document?.getElementById(`${id}-table`);
        if (icon.classList.contains('rotate-180')) icon.classList.remove('rotate-180');
        else icon.classList.add('rotate-180');

        if (table.classList.contains('d-none')) table.classList.remove('d-none');
        else table.classList.add('d-none');
    };

    const addNew = async () => {
        // setIsLoading(true)
        await post('/allergy/create', { doctor: visitDetails?.doctor, item: searchTerm, type: type })
            .then((res) => {
                if (res?.status == 200) {
                    dispatch(getAllergyList())
                    setShowModal(false)
                }
            }).finally(() => {
                // setIsLoading(false)
                setShowModal(false)
            })
    }

    return (
        <div className="allergy-history-section">
            <div className='allergy-history-search-section border-bottom '>
                <div className='header mb-0 pb-0'>
                    <p className='mb-0 pb-0'>Allergy history</p>
                </div>
                <input
                    className="appointment-search mb-0 pb-0"
                    placeholder="Search allergy"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value.toLowerCase())
                    }}
                ></input>
            </div>
            <div className="all-allergy">
                {allergyListWithType?.some(
                    (group) =>
                        group?.allergy?.filter((data) =>
                            data?.item?.toLowerCase()?.includes(searchTerm)
                        ).length > 0
                ) ? (
                    allergyListWithType?.map((allergyGroup, index) => {
                        const filteredAllergies = allergyGroup?.allergy?.filter((data) =>
                            data?.item?.toLowerCase()?.includes(searchTerm)
                        );

                        if (filteredAllergies.length === 0) return null;

                        return (
                            <div key={index} className="type-data">
                                <div
                                    className="d-flex justify-content-between title"
                                    onClick={() => contentHideFun(allergyGroup?.type)}
                                >
                                    <p>{allergyGroup?.type.toUpperCase()}</p>
                                    <FontAwesomeIcon
                                        id={`${allergyGroup?.type}-icon`}
                                        size="sm"
                                        className="mt-1 mr-2"
                                        icon={faChevronUp}
                                    />
                                </div>

                                <table className="table table-sm table-hover mt-0 pt-0">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Reaction</th>
                                            <th>Severity</th>
                                            <th>Source</th>
                                        </tr>
                                    </thead>
                                    <tbody id={`${allergyGroup?.type}-table`}>
                                        {filteredAllergies.map((data, idx) => (
                                            <tr key={idx}>
                                                <td className="align-middle">
                                                    <div className="d-flex">
                                                        <input
                                                            type="checkbox"
                                                            id={`allergy-${data._id}`}
                                                            checked={!!info[data._id]}
                                                            onChange={(e) =>
                                                                handleAllergyChange(
                                                                    data._id,
                                                                    'allergy',
                                                                    data._id,
                                                                    e.target.checked,
                                                                    data?.item,
                                                                    allergyGroup.type
                                                                )
                                                            }
                                                        />
                                                        <label
                                                            htmlFor={`allergy-${data._id}`}
                                                            className="ml-2 my-auto"
                                                        >
                                                            {data?.item}
                                                        </label>
                                                    </div>
                                                </td>
                                                <td className="align-middle">
                                                    <textarea
                                                        className="form-control w-100"
                                                        rows={1}
                                                        value={info[data._id]?.reaction || ''}
                                                        onChange={(e) =>
                                                            handleAllergyChange(
                                                                data._id,
                                                                'reaction',
                                                                e.target.value,
                                                                true,
                                                                data?.item,
                                                                allergyGroup.type
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="align-middle">
                                                    <select
                                                        value={info[data._id]?.severity || ''}
                                                        onChange={(e) =>
                                                            handleAllergyChange(
                                                                data._id,
                                                                'severity',
                                                                e.target.value,
                                                                true,
                                                                data?.item,
                                                                allergyGroup.type
                                                            )
                                                        }
                                                    >
                                                        <option value="">Select</option>
                                                        <option value="mild">Mild</option>
                                                        <option value="moderate">Moderate</option>
                                                        <option value="severe">Severe</option>
                                                    </select>
                                                </td>
                                                <td className="align-middle">
                                                    <select
                                                        value={info[data._id]?.source || ''}
                                                        onChange={(e) =>
                                                            handleAllergyChange(
                                                                data._id,
                                                                'source',
                                                                e.target.value,
                                                                true,
                                                                data?.item,
                                                                allergyGroup.type
                                                            )
                                                        }
                                                    >
                                                        <option value="">Select</option>
                                                        <option value="referral doctors">Referral doctors</option>
                                                        <option value="patient self-reported">
                                                            Patient self-reported
                                                        </option>
                                                        <option value="from Transition of Care">
                                                            From Transition of Care
                                                        </option>
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })
                ) : (

                    <div className='d-flex mx-1'>
                        <p onClick={() => { setShowModal(true) }} className='px-2 mt-1' style={{ backgroundColor: "#14A67D", color: "white", cursor: "pointer", fontSize: "13px" }}>({searchTerm}) Add</p></div>
                )}
            </div>


            {/* <div className="all-allergy">
                {allergyListWithType?.map((allergyGroup, index) => {
                    const filteredAllergies = allergyGroup?.allergy?.filter((data) =>
                        data?.item?.toLowerCase()?.includes(searchTerm)
                    );

                    if (filteredAllergies.length === 0) return null;

                    return (
                        <div key={index} className="type-data">
                            <div
                                className="d-flex justify-content-between title"
                                onClick={() => contentHideFun(allergyGroup?.type)}
                            >
                                <p>{allergyGroup?.type.toUpperCase()}</p>
                                <FontAwesomeIcon
                                    id={`${allergyGroup?.type}-icon`}
                                    size="sm"
                                    className="mt-1 mr-2"
                                    icon={faChevronUp}
                                />
                            </div>

                            <table className="table table-sm table-hover mt-0 pt-0">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Reaction</th>
                                        <th>Severity</th>
                                        <th>Source</th>
                                    </tr>
                                </thead>
                                <tbody id={`${allergyGroup?.type}-table`}>
                                    {filteredAllergies.map((data, idx) => (
                                        <tr key={idx}>
                                            <td className="align-middle">
                                                <div className="d-flex">
                                                    <input
                                                        type="checkbox"
                                                        id={`allergy-${data._id}`}
                                                        checked={!!info[data._id]}
                                                        onChange={(e) =>
                                                            handleAllergyChange(
                                                                data._id,
                                                                'allergy',
                                                                data._id,
                                                                e.target.checked,
                                                                data?.item,
                                                                allergyGroup.type
                                                            )
                                                        }
                                                    />
                                                    <label htmlFor={`allergy-${data._id}`} className="ml-2 my-auto">
                                                        {data?.item}
                                                    </label>
                                                </div>
                                            </td>
                                            <td className="align-middle">
                                                <textarea
                                                    className="form-control w-100"
                                                    rows={1}
                                                    value={info[data._id]?.reaction || ''}
                                                    onChange={(e) =>
                                                        handleAllergyChange(
                                                            data._id,
                                                            'reaction',
                                                            e.target.value,
                                                            true,
                                                            data?.item,
                                                            allergyGroup.type
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td className="align-middle">
                                                <select
                                                    value={info[data._id]?.severity || ''}
                                                    onChange={(e) =>
                                                        handleAllergyChange(
                                                            data._id,
                                                            'severity',
                                                            e.target.value,
                                                            true,
                                                            data?.item,
                                                            allergyGroup.type
                                                        )
                                                    }
                                                >
                                                    <option value="">Select</option>
                                                    <option value="mild">Mild</option>
                                                    <option value="moderate">Moderate</option>
                                                    <option value="severe">Severe</option>
                                                </select>
                                            </td>
                                            <td className="align-middle">
                                                <select
                                                    value={info[data._id]?.source || ''}
                                                    onChange={(e) =>
                                                        handleAllergyChange(
                                                            data._id,
                                                            'source',
                                                            e.target.value,
                                                            true,
                                                            data?.item,
                                                            allergyGroup.type
                                                        )
                                                    }
                                                >
                                                    <option value="">Select</option>
                                                    <option value="referral doctors">Referral doctors</option>
                                                    <option value="patient self-reported">Patient self-reported</option>
                                                    <option value="from Transition of Care">From Transition of Care</option>
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </div> */}

            <CustomModal
                size="md"
                show={showModal}
                isHeaderHide={true}
            >
                <div className="p-4">

                    {/* Modal Content */}
                    <div>
                        <div className="form-group mb-3">
                            <label style={{ fontSize: "14px" }} htmlFor="allergyName">Allergy Name</label>
                            <input
                                style={{ fontSize: "14px" }}
                                value={searchTerm}
                                type="text"
                                id="allergyName"
                                className="form-control"
                                placeholder="Enter allergy name"
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label style={{ fontSize: "14px" }} htmlFor="allergyType">Allergy Type</label>
                            <select value={type} onChange={(e) => { setType(e.target.value) }} style={{ fontSize: "14px" }} id="allergyType" className="form-control" defaultValue="option1">
                                <option value="medicine">Medicine </option>
                                <option value="food">Food</option>
                                <option value="environment">Environment</option>
                            </select>
                        </div>
                        <div className='d-flex justify-content-end'>
                            <div className='d-flex mx-1'>
                                <p onClick={() => { setShowModal(false) }} className='px-2 mt-1' style={{ backgroundColor: "red", color: "white", cursor: "pointer", fontSize: "13px" }}>Close</p></div>
                            <div className='d-flex mx-1'>
                                <p onClick={() => { addNew() }} className='px-2 mt-1' style={{ backgroundColor: "#14A67D", color: "white", cursor: "pointer", fontSize: "13px" }}>Add</p></div>
                        </div>
                    </div>
                </div>
            </CustomModal>

        </div>
    );
};

export default AllergyHistory;
